import _ from 'lodash';
import path from 'path';
import mime from 'mime';
import { cognitoMixins } from '@storm/mixins/cognito';

const uploader = {
  mixins: [cognitoMixins],
  data() {
    return {
      uploadList: [],
    };
  },
  methods: {
    async uploadFile(p, bp) {
      const {
        Bucket = process.env.S3_WEBSITE_BUCKET,
        ACL = 'public-read',
        copyToOwner = true,
        bucketPath = `${this._uploadFolder}/`,
      } = p;
      // check for expired sessions first
      if (this._storm) {
        await this.checkStormSession();
      } else {
        await this._cognitoCheckSession();
      }
      const s3 = new this.$aws.S3({ region: process.env.S3_REGION });
      // build payload to send to s3
      let Key = `${bucketPath}${p.file}`;
      if (bp) {
        const preFixFolder = new RegExp(`sites/${this._currentSite.name}/`, 'gi');
        const filePath = p.file.replace(preFixFolder, '');
        Key = `boilerplates/${bp.boilerplate.name}/${filePath}`;
      }
      const params = {
        Bucket,
        Body: p.body,
        ACL,
        Key, // file path to place in bucket
        ContentType: mime.getType(p.file),
        ServerSideEncryption: 'AES256',
      };

      const id = Date.now();
      const activeFile = {
        id,
        file: path.basename(p.file),
        progress: '0%',
        message: 'Uploading...',
        status: 'uploading',
        s3: null,
      };
      this.uploadList.push(activeFile);
      const file = await new Promise((resolve, reject) => {
        const upload = s3.upload(params);
        activeFile.s3 = upload;
        upload.on('httpUploadProgress', (progress) => {
          // api: https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/Request.html#httpUploadProgress-event
          // update progress data for visuals (progress bars etc.)
          activeFile.progress = `${parseInt((progress.loaded * 100) / progress.total, 10)}%`;
        });
        upload.send((error, data) => {
          if (error) {
            const message = error.message || error;
            if (message.match(/aborted/gi)) {
              this.removeUpload(id);
            }
            activeFile.status = 'error';
            if (this.$demo) {
              activeFile.message = 'Uploading disabled in demo mode.';
            } else {
              activeFile.message = message;
            }
            reject(error);
            return;
          }
          activeFile.status = 'success';
          activeFile.message = 'Upload Complete!';
          setTimeout(() => {
            this.removeUpload(id);
          }, 1000);
          resolve(data);
        });
      });

      if (p.thumbnail) {
        // compress the image into a thumbnail & send to s3
        await this.makeThumbnail(p.thumbnail, params, copyToOwner);
      }

      // if a sub-user, we need to move the file from the subusers dir to the owners
      if ((this.currentUser.subuser || !this._siteOwner)
          && this._currentSite._id
          && copyToOwner) {
        const move = await this.$lightning([{
          name: 'moveToOwnerBucket',
          payload: {
            site: {
              _id: this._currentSite._id,
              alias: this._currentSite.alias,
            },
            file,
          },
        }]);
        file.Location = move.location;
      }

      return file;
    },
    removeUpload(id) {
      const i = _.find(this.uploadList, item => item.id === id);
      this.uploadList[i] = this.uploadList[this.uploadList.length - 1];
      this.uploadList.pop();
    },
    replaceExt(params) {
      const pos = params.file.lastIndexOf('.');
      return params.file.substr(0, pos < 0 ? params.file.length : pos) + params.ext;
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },
    async makeThumbnail(options, params, copyToOwner) {
      const s3 = new this.$aws.S3({ region: process.env.S3_REGION });
      const image = options.srcFile;
      const w = options.width;
      let type = path.extname(options.name);
      type = type.replace('.', '');
      const ext = type;
      if (type === 'svg') {
        type = 'svg+xml';
      }
      // create a canvas for creating the thumbs
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = w;

      let media;
      let video = false;
      let objFile = null;

      if (['mov', 'mp4', 'mpg', 'mpeg', 'avi'].includes(type)) {
        video = true;
        objFile = URL.createObjectURL(image);
        // for video's types
        media = document.createElement('video');
        media.setAttribute('src', objFile);
        media.setAttribute('muted', true);
        media.setAttribute('autoplay', true);
        media.setAttribute('playsinline', true);
        // load video data so we can create a thumbnail
        await new Promise((r) => { media.addEventListener('loadeddata', () => r()); });
      } else {
        // for all other image types
        const base64File = await this.fileToBase64(image);
        media = new Image(w, w);
        media.src = base64File;
        await new Promise((r) => { media.onload = () => r(); });
      }
      canvas.getContext('2d').drawImage(media, 0, 0, w, w);

      const base64Thumb = canvas.toDataURL('image/png');
      const decodedThumb = base64Thumb.replace(/^data:image\/\w+;base64,/, '');
      const thumbFile = `${path.basename(options.name, path.extname(options.name))}__${ext}__.png`;
      // start thumbnail upload
      const thumb = _.extend(_.cloneDeep(params), {
        Key: `${this._uploadFolder}/${options.bucketPath}${thumbFile}`,
        Body: Buffer.from(decodedThumb, 'base64'),
        ContentEncoding: 'base64',
        ContentType: mime.getType(thumbFile),
      });

      await this._cognitoCheckSession();
      const file = await s3.upload(thumb).promise();

      if ((this.currentUser.subuser || !this._siteOwner)
          && this._currentSite._id
          && copyToOwner) {
        await this.$lightning([{
          name: 'moveToOwnerBucket',
          payload: {
            site: {
              _id: this._currentSite._id,
              alias: this._currentSite.alias,
            },
            file,
          },
        }]);
      }

      if (video && media) {
        media.remove();
      }

      if (objFile) {
        URL.revokeObjectURL(objFile);
      }
    },
  },
};

export { uploader };



// WEBPACK FOOTER //
// ./src/mixins/aws/uploader/s3.js